import BaseMap from 'components/BaseMap/BaseMap';
import { useMemo } from 'react';
import {
  HURRICANES_DATASETS,
  HURRICANES_DEFAULT_REPLAY_MARKER_MODE,
} from '../../config/hurricanes.config';
import {
  buildForecastCone,
  buildHistoricalStormOutline,
  buildStormTrackSegments,
  toLatLng,
} from '../../utils/hurricaneGeometry.utils';
import { getStormRecordAtFrame } from '../../utils/hurricanes.utils';
import HurricaneMapLayers from './components/HurricaneMapLayers/HurricaneMapLayers';

// Composes BaseMap with the storm layers. The expensive smoothed polygons and
// track paths are memoized per storm/dataset so replay frame changes only
// recompute the visible markers.
const HurricaneMapContent = ({
  stormGroups = [],
  selectedStorm = null,
  dataset = HURRICANES_DATASETS.live,
  frameIndex = null,
  markerMode = HURRICANES_DEFAULT_REPLAY_MARKER_MODE,
  fallbackCenter = null,
  scrollZoom = true,
}) => {
  const stormShapes = useMemo(
    () =>
      stormGroups.map((group) => {
        const { observedTrackPositions, forecastTrackPositions } = buildStormTrackSegments(
          group.records,
        );

        return {
          key: group.key,
          group,
          observedTrackPositions,
          forecastTrackPositions,
          conePositions:
            dataset === HURRICANES_DATASETS.live ? buildForecastCone(group.records) : null,
          outlinePositions:
            dataset === HURRICANES_DATASETS.historical
              ? buildHistoricalStormOutline(group.records)
              : null,
        };
      }),
    [stormGroups, dataset],
  );

  // Keep the storm's active position centered: follow the replay frame while
  // scrubbing/playing, otherwise the storm's latest position. BaseMap re-syncs
  // its view whenever this value changes, so no map remount is needed.
  const mapCenter = useMemo(() => {
    const activeRecord = getStormRecordAtFrame(selectedStorm?.records, frameIndex);
    const stormCoords = activeRecord
      ? toLatLng(activeRecord.latitude, activeRecord.longitude)
      : null;
    return stormCoords ?? fallbackCenter ?? undefined;
  }, [selectedStorm, frameIndex, fallbackCenter]);

  return (
    <div className="position-relative flex-fill w-100 h-100 rounded-2 overflow-hidden">
      <BaseMap
        mapCenter={mapCenter}
        zoom={5}
        preserveLocationZoom
        scrollZoom={scrollZoom}
        style={{ zIndex: 0 }}
      >
        <HurricaneMapLayers
          stormShapes={stormShapes}
          frameIndex={selectedStorm ? frameIndex : null}
          markerMode={markerMode}
        />
      </BaseMap>
    </div>
  );
};

export default HurricaneMapContent;
